import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell, MobileMenu } from "../components/Layout";
import api, { resolveAssetUrl } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import usePlatform from "../hooks/usePlatform";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { Sun, Heart, List, Wallet, Plus, Sparkle, SignIn } from "@phosphor-icons/react";
import PortalAuthModal from "../components/PortalAuthModal";

/**
 * NorthIndianKundliIcon — inline SVG of the classic North Indian
 * kundali chart used for the "Free Kundli" tile. A square split by
 * a diamond (rhombus connecting the midpoints of each side) with
 * both main diagonals, producing the recognisable 12-house layout.
 *
 * Kept as an inline component so the tile stays a single file and
 * we don't ship a separate icon-font asset for one glyph.
 */
function NorthIndianKundliIcon({ size = 32, color = "#3F2D1B" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Outer square (the chart's frame) */}
      <rect x="6" y="6" width="52" height="52" rx="2" />
      {/* Both diagonals — corner to corner */}
      <line x1="6"  y1="6"  x2="58" y2="58" />
      <line x1="58" y1="6"  x2="6"  y2="58" />
      {/* Inner diamond — connects midpoints of the four sides */}
      <path d="M32 6 L58 32 L32 58 L6 32 Z" />
    </svg>
  );
}

/**
 * AppHomePage — the Android-app-only landing screen.
 *
 * Rendered instead of `LandingPage` when the WebView reports
 * `window.Capacitor?.isNativePlatform()` is true (see routing in
 * App.js).
 *
 * v2 (post-user-feedback):
 *   • Switched to the cream/warm brand theme (bg-background,
 *     text-foreground) instead of the earlier night-sky purple — now
 *     matches the rest of the WebView so users don't experience a
 *     jarring theme shift when navigating into a tool page.
 *   • Fixed `a.id` → `a.astrologer_id` in the astrologer grid — the
 *     detail page was stuck on "Loading..." because /astrologers/undefined
 *     never resolves.
 *   • Rotating banner now uses a themed BACKGROUND IMAGE per topic
 *     (Indian marriage, career desk, couple, coins, family) with a
 *     dark overlay to keep text readable.
 *   • All copy text uses dark warm-brown (text-foreground) or the
 *     high-contrast accent colors so nothing disappears against the
 *     cream background.
 */

// ── Rotating banners ──────────────────────────────────────────────
// Each entry pairs a short bilingual question with a topic-matched
// Unsplash background image (all cc-free). `overlay` is a dark
// gradient tint used to keep the white text readable against any
// brightness in the source photo.
const BANNERS = [
  {
    key: "career",
    hindi: "Job switch ka sahi time kab hai?",
    english: "Career timing decoded by expert astrologers.",
    accent: "#F59E0B",
    cta: "Ask a Career Expert",
    route: "/astrologers?category=career",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=900&q=70&auto=format&fit=crop",
    overlay: "linear-gradient(90deg, rgba(20,10,0,0.72) 0%, rgba(20,10,0,0.42) 100%)",
  },
  {
    key: "marriage",
    hindi: "Shaadi kab hogi? Sitaron se poocho abhi.",
    english: "Your exact marriage timeline — read your kundli today.",
    accent: "#EC4899",
    cta: "Ask about Marriage",
    route: "/astrologers?category=marriage",
    image: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=900&q=70&auto=format&fit=crop",
    overlay: "linear-gradient(90deg, rgba(60,0,20,0.72) 0%, rgba(60,0,20,0.42) 100%)",
  },
  {
    key: "love",
    hindi: "Pyaar mein dhokha ya sacchi mohabbat?",
    english: "What your partner's chart really reveals.",
    accent: "#DB2777",
    cta: "Love Reading",
    route: "/astrologers?category=love",
    image: "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=900&q=70&auto=format&fit=crop",
    overlay: "linear-gradient(90deg, rgba(50,0,30,0.72) 0%, rgba(50,0,30,0.42) 100%)",
  },
  {
    key: "wealth",
    hindi: "Paisa kab aayega? Grahon ka rukh dekho.",
    english: "Financial gains, investments, delays — read the stars.",
    accent: "#10B981",
    cta: "Wealth Consultation",
    route: "/astrologers?category=wealth",
    image: "https://images.unsplash.com/photo-1621886292650-520f76c747d6?w=900&q=70&auto=format&fit=crop",
    overlay: "linear-gradient(90deg, rgba(0,30,15,0.72) 0%, rgba(0,30,15,0.42) 100%)",
  },
  {
    key: "family",
    hindi: "Ghar mein tension kyun hai? Doshon ki jaanch karo.",
    english: "Family issues, delays, disputes — remedies from verified experts.",
    accent: "#8B5CF6",
    cta: "Family Guidance",
    route: "/astrologers?category=family",
    image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=900&q=70&auto=format&fit=crop",
    overlay: "linear-gradient(90deg, rgba(25,0,50,0.72) 0%, rgba(25,0,50,0.42) 100%)",
  },
];

const TILES = [
  {
    label: "Daily Horoscope",
    Icon: Sun,
    route: "/horoscope",
    bg: "linear-gradient(135deg, #FFD66B 0%, #FFB800 100%)",
    text: "#3F2D1B",
  },
  {
    label: "Free Kundli",
    Icon: NorthIndianKundliIcon,
    route: "/kundali-calculator",
    bg: "linear-gradient(135deg, #FEC260 0%, #E89200 100%)",
    text: "#3F2D1B",
  },
  {
    label: "Kundli Matching",
    Icon: Heart,
    route: "/compatibility",
    bg: "linear-gradient(135deg, #F7E7B8 0%, #FFD66B 100%)",
    text: "#3F2D1B",
  },
];

export default function AppHomePage() {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { user, logout } = useAuth();
  const { isIOSApp } = usePlatform();
  const [astrologers, setAstrologers] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  // App Review demo account opens straight onto the signature-features tour
  // (Guideline 4.3(b) walkthrough). "/" inside the native shell renders THIS
  // page, not LandingPage — so the redirect must live here too.
  useEffect(() => {
    if (user?.email === "reviewer.astrokuberchat@gmail.com") {
      navigate("/showcase", { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = () => {
    // Kept for backwards-compat (MobileMenu may call it), but new default
    // is the unified Portal modal that shows Google + email in one place.
    setAuthOpen(true);
  };

  // ── Fetch 6 astrologers on mount ──
  useEffect(() => {
    let cancelled = false;
    api
      .get("/astrologers")
      .then(({ data }) => {
        if (cancelled) return;
        const online = (data || []).filter((a) => a.is_online || a.is_ai);
        const others = (data || []).filter((a) => !a.is_online && !a.is_ai);
        setAstrologers([...online, ...others].slice(0, 6));
      })
      .catch(() => setAstrologers([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Rotate banners every 10 seconds ──
  useEffect(() => {
    const timer = setInterval(() => {
      setBannerIdx((prev) => (prev + 1) % BANNERS.length);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const banner = BANNERS[bannerIdx];

  const dots = useMemo(
    () => BANNERS.map((_, i) => i === bannerIdx),
    [bannerIdx],
  );

  return (
    <PageShell hideHeader>
      <div
        data-testid="app-home"
        className="min-h-screen pb-24"
        style={{ background: "#FEF9EC", color: "#3F2D1B" }}
      >
        {/* ── Top bar: circular menu button (Innovative-branded) + wallet pill ── */}
        <div
          className="flex items-center justify-between gap-3 px-4 pb-4"
          style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
          data-testid="app-home-topbar"
        >
          {/* Left: circular gold-gradient menu button + "Innovative" caption */}
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              data-testid="app-home-menu-btn"
              className="relative w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-transform shadow-md"
              style={{
                background: "linear-gradient(135deg, #F7D774 0%, #E8A628 55%, #C97C0A 100%)",
              }}
            >
              <List size={22} weight="bold" style={{ color: "#3F2D1B" }} />
              {/* Sparkle badge — visual cue that this button = Innovative brand */}
              <span
                className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: "#3F2D1B" }}
                aria-hidden="true"
              >
                <Sparkle size={9} weight="fill" style={{ color: "#F7D774" }} />
              </span>
            </button>
            <div className="flex flex-col leading-none">
              <span
                className="text-[10px] font-bold tracking-[0.18em] uppercase"
                style={{ color: "#C97C0A" }}
              >
                Innovative
              </span>
              <span
                className="text-[10px] italic mt-0.5"
                style={{ color: "#8D857A" }}
              >
                Astrology
              </span>
            </div>
          </div>

          {/* Right: Sign-in pill (guests only) + wallet pill */}
          <div className="flex items-center gap-2">
            {!user && (
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                data-testid="app-home-signin-btn"
                aria-label="Sign in"
                className="flex items-center gap-1.5 pl-3 pr-3 py-1.5 rounded-full active:scale-95 transition-transform shadow-md"
                style={{
                  background: "linear-gradient(135deg, #F7D774 0%, #E8A628 55%, #C97C0A 100%)",
                  color: "#3F2D1B",
                  border: "1px solid rgba(201, 124, 10, 0.35)",
                }}
              >
                <SignIn size={16} weight="bold" />
                <span className="text-sm font-bold">Sign in</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => user ? navigate("/dashboard#wallet") : setAuthOpen(true)}
              data-testid="app-home-wallet-pill"
              aria-label={user ? "Open wallet" : "Sign in to add balance"}
              className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white shadow-md active:scale-95 transition-transform"
              style={{ border: "1px solid rgba(201, 124, 10, 0.18)" }}
            >
              <Wallet size={16} weight="duotone" style={{ color: "#C97C0A" }} />
              <span
                className="text-sm font-semibold tabular-nums"
                style={{ color: "#3F2D1B" }}
              >
                {user ? formatPrice(Number(user?.wallet_balance || 0)) : "Free"}
              </span>
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: "linear-gradient(135deg, #F7D774 0%, #E8A628 55%, #C97C0A 100%)",
                }}
                aria-hidden="true"
              >
                <Plus size={14} weight="bold" style={{ color: "#3F2D1B" }} />
              </span>
            </button>
          </div>
        </div>

        <div className="px-4">
          {/* ── iOS-only differentiation banner (Apple Guideline 4.3(b)) — leads
              the app home with the world's-first claim. Web + Android unchanged. ── */}
          {isIOSApp && (
            <button
              type="button"
              data-testid="app-home-worldfirst-banner"
              onClick={() => navigate("/astro-visualization")}
              className="w-full rounded-2xl mb-4 text-left active:scale-[.98] transition-transform overflow-hidden relative shadow-lg p-4"
              style={{ background: "linear-gradient(120deg, #3F2D1B 0%, #6b4a2a 55%, #C97C0A 100%)" }}
            >
              <span
                className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] uppercase rounded-full px-2.5 py-1"
                style={{ background: "rgba(247,215,116,0.16)", color: "#F7D774", border: "1px solid rgba(247,215,116,0.4)" }}
              >
                <Sparkle size={10} weight="fill" /> World's First
              </span>
              <div
                className="font-serif-display text-2xl leading-tight mt-2"
                style={{ color: "#FEF9EC" }}
              >
                Astro Visualization Platform
              </div>
              <p className="text-xs mt-1.5 leading-relaxed" style={{ color: "rgba(254,249,236,0.78)" }}>
                Watch your entire life unfold as a cinematic Mahadasha map — scrub
                year by year through your destiny. Only on AstroKuberChat.
              </p>
              <span
                className="inline-flex items-center gap-1 text-xs font-bold mt-3 rounded-full px-3 py-1.5"
                style={{ background: "#F7D774", color: "#3F2D1B" }}
              >
                Visualise my life →
              </span>
            </button>
          )}

          {/* ── 3 hero tiles ── */}
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="app-home-tiles">
            {TILES.map((tile) => {
              const TileIcon = tile.Icon;
              return (
                <button
                  key={tile.label}
                  data-testid={`tile-${tile.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => navigate(tile.route)}
                  className="rounded-2xl aspect-square flex flex-col items-center justify-center p-2 active:scale-95 transition-transform shadow-md"
                  style={{ background: tile.bg, color: tile.text }}
                >
                  <TileIcon size={32} weight="fill" />
                  <span className="text-xs font-semibold text-center leading-tight mt-2">
                    {tile.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Rotating marketing banner with themed background image ── */}
          <button
            data-testid="app-home-banner"
            onClick={() => navigate(banner.route)}
            className="w-full rounded-2xl mb-6 text-left active:scale-[.98] transition-transform overflow-hidden relative shadow-lg"
            style={{ minHeight: 170 }}
          >
            {/* Background image */}
            <img
              key={banner.key}
              src={banner.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                // Graceful fallback: if the CDN image fails, hide it —
                // the coloured overlay is designed to look presentable
                // on its own too.
                e.currentTarget.style.display = "none";
              }}
            />
            {/* Dark overlay for text contrast */}
            <div
              className="absolute inset-0"
              style={{ background: banner.overlay }}
            />
            {/* Content */}
            <div className="relative p-5 z-10">
              <div
                className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full mb-2"
                style={{ background: banner.accent, color: "#1A0B2E" }}
              >
                {banner.key}
              </div>
              <div className="text-white text-lg font-bold leading-snug mb-1 drop-shadow-lg">
                {banner.hindi}
              </div>
              <div className="text-white/85 text-xs leading-relaxed mb-3 max-w-[85%] drop-shadow">
                {banner.english}
              </div>
              <span
                className="inline-block text-xs font-bold rounded-full px-3.5 py-2 shadow-md"
                style={{ background: banner.accent, color: "#1A0B2E" }}
              >
                {banner.cta} →
              </span>
            </div>
            {/* Progress dots */}
            <div className="relative z-10 flex justify-center gap-1.5 pb-3">
              {dots.map((active, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBannerIdx(i);
                  }}
                  aria-label={`Banner ${i + 1}`}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: active ? 20 : 6,
                    background: active ? "#fff" : "rgba(255,255,255,0.4)",
                  }}
                />
              ))}
            </div>
          </button>

          {/* ── Start your free chat now ── */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-extrabold" style={{ color: "#3F2D1B" }}>
              Start your free chat now
            </h2>
            <button
              data-testid="app-home-see-more-astrologers"
              onClick={() => navigate("/astrologers")}
              className="text-xs font-semibold underline underline-offset-2"
              style={{ color: "#E89200" }}
            >
              See all →
            </button>
          </div>

          {astrologers.length === 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[3/4] rounded-2xl animate-pulse"
                  style={{ background: "rgba(63,45,27,0.08)" }}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="app-home-astrologer-grid">
              {astrologers.map((a) => (
                <button
                  key={a.astrologer_id}
                  data-testid={`astrologer-card-${a.astrologer_id}`}
                  onClick={() => navigate(`/astrologers/${a.astrologer_id}`)}
                  className="rounded-2xl overflow-hidden text-left active:scale-[.97] transition-transform"
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(63,45,27,0.12)",
                    boxShadow: "0 2px 12px -6px rgba(63,45,27,0.15)",
                  }}
                >
                  <div className="aspect-[4/5] relative" style={{ background: "#F5EEDE" }}>
                    <img
                      src={resolveAssetUrl(a.avatar)}
                      alt={a.name}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                    {(a.is_online || a.is_ai) && (
                      <span
                        className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full shadow"
                        style={{ background: "#10B981", color: "#0A2015" }}
                      >
                        {a.is_ai ? "AI · FREE" : "ONLINE"}
                      </span>
                    )}
                  </div>
                  <div className="p-2.5" style={{ background: "#FFFFFF" }}>
                    <div
                      className="font-semibold text-sm truncate"
                      style={{ color: "#3F2D1B" }}
                    >
                      {a.name}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: "#8D857A" }}>
                      {a.experience_years ? `${a.experience_years}y exp` : "Verified"}
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs font-bold" style={{ color: "#E89200" }}>
                        {a.is_ai ? "Free chat" : `${formatPrice(a.rate_per_min)}/min`}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* ── More astrologers CTA ── */}
          <button
            data-testid="app-home-browse-all"
            onClick={() => navigate("/astrologers")}
            className="w-full mt-6 py-3.5 rounded-full font-bold text-sm active:scale-[.98] transition-transform shadow-md"
            style={{
              background: "linear-gradient(90deg, #FFB800 0%, #E89200 100%)",
              color: "#1A0B2E",
            }}
          >
            Browse all astrologers →
          </button>
        </div>
      </div>

      {/* Slide-in side menu triggered by the circular menu button at
          the top-left of the AppHome top bar. Uses the same MobileMenu
          component the rest of the app uses so the item list, VIP
          banner, wallet pill and sign-out button all stay identical. */}
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
        onLogin={handleLogin}
        onLogout={logout}
        onOpenVipOffer={() => {
          setMenuOpen(false);
          navigate("/dashboard#vip");
        }}
      />
      <PortalAuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={() => setAuthOpen(false)}
      />
    </PageShell>
  );
}
